import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Loader2, Image as ImageIcon, ExternalLink, PlayCircle, Film,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AD_CREATIVE_REPORT_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";
import {
    AdFilterBar, EmptyState, StatTile, money, num, pct,
    rankingLabel, rankingTone, resolveDateRange,
    StatusPill, isDelivering,
    StatusFilterSelect,
} from "./adReportKit";

const SORTS = [
    { value: "spend", label: "Spend" },
    { value: "meta_leads", label: "Leads" },
    { value: "cpl", label: "Cheapest CPL" },
    { value: "thumbstop_rate", label: "Thumb-stop rate" },
    { value: "impressions", label: "Impressions" },
];

/** Video drop-off as four proportional bars against play count. */
const VideoFunnel = ({ funnel }) => {
    const base = funnel?.plays || 0;
    if (!base) return <span className="text-[11px] text-muted-foreground">Not a video</span>;
    const steps = [
        { k: "p25", label: "25%" },
        { k: "p50", label: "50%" },
        { k: "p75", label: "75%" },
        { k: "p100", label: "100%" },
    ];
    return (
        <div className="flex items-end gap-1 h-9" title={`${base.toLocaleString("en-IN")} plays`}>
            {steps.map((s) => {
                const v = funnel[s.k] || 0;
                const h = Math.max((v * 100) / base, v > 0 ? 4 : 0);
                return (
                    <div key={s.k} className="flex flex-col items-center gap-0.5 flex-1">
                        <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-sm flex items-end h-6">
                            <div className="w-full bg-violet-500 rounded-sm" style={{ height: `${h}%` }} />
                        </div>
                        <span className="text-[8px] text-muted-foreground">{s.label}</span>
                    </div>
                );
            })}
        </div>
    );
};

export default function AdCreativeReport() {
    const { fetch } = useApiMethods();

    const [summary, setSummary] = useState(null);
    const [ads, setAds] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);

    const [datePreset, setDatePreset] = usePersistedState("ad_cr_preset", "last_month");
    const [customFrom, setCustomFrom] = usePersistedState("ad_cr_from", "");
    const [customTo, setCustomTo] = usePersistedState("ad_cr_to", "");
    const [accountId, setAccountId] = usePersistedState("ad_cr_account", "");
    const [sort, setSort] = usePersistedState("ad_cr_sort", "spend");
    const [minSpend, setMinSpend] = usePersistedState("ad_cr_minspend", "");
    const [statusFilter, setStatusFilter] = usePersistedState("ad_cr_status", "");
    const [search, setSearch] = useState("");

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const p = new URLSearchParams();
        if (from_date) p.append("from_date", from_date);
        if (to_date) p.append("to_date", to_date);
        if (accountId) p.append("account_id", accountId);
        if (sort) p.append("sort", sort);
        if (minSpend) p.append("min_spend", minSpend);
        if (statusFilter) p.append("status", statusFilter);
        if (search.trim()) p.append("search", search.trim());

        fetch(`${AD_CREATIVE_REPORT_URL}?${p.toString()}`)
            .then((res) => {
                if (res?.status === "success") {
                    setSummary(res.summary || null);
                    setAds(Array.isArray(res.data) ? res.data : []);
                    setAccounts(res.filters?.accounts || []);
                } else {
                    ToastMsg("error", res?.message || "Failed to load creative report");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load creative report"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, accountId, sort, minSpend, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const f = summary?.video_funnel;

    return (
        <ContainerCard>
            <div className="p-2 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <ImageIcon className="h-6 w-6 text-primary" />
                            Ad Creative
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Which creative is actually winning — thumbnail, copy, Meta's rankings and video hold.
                        </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                        <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                        Refresh
                    </Button>
                </div>

                <AdFilterBar
                    {...{ datePreset, setDatePreset, customFrom, setCustomFrom, customTo, setCustomTo,
                        accountId, setAccountId, accounts, search, setSearch }}
                    onSearch={load}
                    searchPlaceholder="Search ad or headline…"
                >
                    <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                    <select value={sort} onChange={(e) => setSort(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                        {SORTS.map((s) => <option key={s.value} value={s.value}>Sort: {s.label}</option>)}
                    </select>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                        <option value="">Any status</option>
                        <option value="ACTIVE">Active only</option>
                        <option value="PAUSED">Paused / stopped</option>
                    </select>
                    <select value={minSpend} onChange={(e) => setMinSpend(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                        <option value="">Any spend</option>
                        <option value="500">Spent ₹500+</option>
                        <option value="2000">Spent ₹2,000+</option>
                        <option value="10000">Spent ₹10,000+</option>
                    </select>
                </AdFilterBar>

                {summary && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <StatTile label="Ads" value={num(summary.ads)}
                            sub={`${summary.ranked} graded by Meta`} />
                        <StatTile tone="spend" label="Spend" value={money(summary.spend)} />
                        <StatTile tone="leads" label="Leads" value={num(summary.leads)} />
                        <StatTile tone="cost" label="Cost / Lead" value={money(summary.cpl)} />
                        <StatTile label="Thumb-stop" value={pct(summary.thumbstop_rate)}
                            hint="Share of impressions that became a video play"
                            sub={f ? `${num(f.plays)} plays` : null} />
                        <StatTile
                            tone={summary.below_average > summary.above_average ? "bad" : "default"}
                            label="Meta grading"
                            value={`${summary.above_average} ▲ / ${summary.below_average} ▼`}
                            sub="above / below average" />
                    </div>
                )}

                {loading && (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                )}

                {!loading && ads.length === 0 && (
                    <EmptyState
                        title={statusFilter === "ACTIVE"
                            ? "No ads are currently delivering in this period"
                            : "No ads delivered in this period"}
                        hint={statusFilter
                            ? "Set the status filter back to “Any status” to see ads that have stopped."
                            : "Widen the date range, or run a sync from Settings → Meta Ads if an account was only just connected."}
                    />
                )}

                {!loading && ads.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {ads.map((ad) => (
                            <div key={ad.ad_id}
                                className={cn("rounded-xl border border-border bg-card overflow-hidden flex flex-col",
                                    !isDelivering(ad.effective_status) && "opacity-70")}>
                                <div className="flex gap-3 p-3">
                                    <div className="h-16 w-16 rounded-lg bg-muted shrink-0 overflow-hidden border border-border">
                                        {ad.thumbnail_url ? (
                                            <img src={ad.thumbnail_url} alt=""
                                                className="h-full w-full object-cover"
                                                onError={(e) => { e.target.style.visibility = "hidden"; }} />
                                        ) : (
                                            <div className="h-full w-full grid place-items-center">
                                                <Film className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-start gap-1.5">
                                            <p className="text-sm font-semibold text-foreground truncate" title={ad.name}>
                                                {ad.name}
                                            </p>
                                            {ad.permalink_url && (
                                                <a href={ad.permalink_url} target="_blank" rel="noreferrer"
                                                    className="text-muted-foreground hover:text-primary shrink-0 mt-0.5">
                                                    <ExternalLink className="h-3 w-3" />
                                                </a>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                            <StatusPill status={ad.effective_status} compact />
                                            <p className="text-[11px] text-muted-foreground truncate" title={ad.campaign_name}>
                                                {ad.campaign_name}
                                            </p>
                                        </div>
                                        {ad.cta && (
                                            <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] bg-primary/10 text-primary">
                                                {ad.cta.replace(/_/g, " ")}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {(ad.title || ad.body) && (
                                    <div className="px-3 pb-2">
                                        {ad.title && <p className="text-xs font-medium text-foreground line-clamp-1">{ad.title}</p>}
                                        {ad.body && <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{ad.body}</p>}
                                    </div>
                                )}

                                <div className="grid grid-cols-4 gap-2 px-3 py-2 border-t border-border">
                                    <div>
                                        <p className="text-[9px] uppercase text-muted-foreground">Spend</p>
                                        <p className="text-xs font-semibold text-foreground tabular-nums">{money(ad.spend)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] uppercase text-muted-foreground">Leads</p>
                                        <p className="text-xs font-semibold text-foreground tabular-nums">{num(ad.meta_leads)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] uppercase text-muted-foreground">CPL</p>
                                        <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 tabular-nums">{money(ad.cpl)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] uppercase text-muted-foreground">CTR</p>
                                        <p className="text-xs font-semibold text-foreground tabular-nums">{pct(ad.ctr)}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-2 px-3 py-2 border-t border-border">
                                    <div>
                                        <p className="text-[9px] uppercase text-muted-foreground mb-1">Video hold</p>
                                        <VideoFunnel funnel={ad.video_funnel} />
                                        {ad.thumbstop_rate != null && (
                                            <p className="text-[9px] text-muted-foreground mt-1 flex items-center gap-1">
                                                <PlayCircle className="h-2.5 w-2.5" />
                                                {pct(ad.thumbstop_rate)} thumb-stop
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[9px] uppercase text-muted-foreground">Meta ranking</p>
                                        {[
                                            ["Quality", ad.quality_ranking],
                                            ["Engagement", ad.engagement_ranking],
                                            ["Conversion", ad.conversion_ranking],
                                        ].map(([label, v]) => (
                                            <p key={label} className="text-[10px] flex justify-between gap-1">
                                                <span className="text-muted-foreground">{label}</span>
                                                <span className={cn("font-medium text-right", rankingTone(v))}>
                                                    {rankingLabel(v)}
                                                </span>
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && ads.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                        {ads.length} ad{ads.length === 1 ? "" : "s"}. Rankings show Meta's most recent grade
                        in the period — they are a relative position, not a quantity, so they are never averaged.
                    </p>
                )}
            </div>
        </ContainerCard>
    );
}
